import * as React from "react";
import { connect, ConnectedProps } from "react-redux";
import { ChoiceGroup, IChoiceGroupOption, IChoiceGroupStyles, IStackItemStyles, IStackStyles, IStackTokens, Label, MessageBar, MessageBarType, PrimaryButton, Stack } from "@fluentui/react";
import { OneDriveOverWriteEnum } from "../../enums/oneDriveOverWriteEnum";
import {
  setOneDriveOverwriteOptionSelected,
  resetDuplicateFilesAndOverWriteSettings
} from "../../../redux/modules/file";
import { resetWorkingDirectories, addWorkingDirectories } from "../../../redux/modules/workingDirectory";
import { setNeedsRedirectToHome } from "../../../redux/modules/savingInBackground";
import { resetLocationBeforeSaving } from "../../../redux/modules/oneDrive";
import { LanguageContext } from "../../../../../utilities/LocalizationModule";
import { RootState } from "../../../redux";
import { DocubirdCache } from "../../../config/constants";
import { CacheHelper } from "../../../../../utilities/CacheHelper";
import { ModeEnum } from "../../enums/modeEnum";

const stackStylesDefault: IStackStyles = {
  root: {
    paddingTop: 15
  }
};
const stackStylesPadded: IStackStyles = {
  root: {
    paddingTop: 15,
    paddingBottom: 15
  }
};
const stackTokens: IStackTokens = {
  childrenGap: 20
};
const choiceGroupStyles: IChoiceGroupStyles = { label: { fontSize: 16 } };

const mapStateToProps = (state: RootState) => {
  return {
    hasDuplicateEmails: state.files.files.some(item => item.isDuplicate && item.isSelected),
    hasLockedCheckedOutFiles: state.files.files.some(item => item.filePreCommitResult?.locked || item.filePreCommitResult?.checkedOut),
    files: state.files.files.filter(item => item.isSelected),
    needsRedirect: state.savingInBackground.needsRedirectToHome,
    oneDriveLocationsBeforeSaving: state.oneDrive.locationBeforeSaving,
    mode: state.mode.mode
  };
};

const mapDispatchToProps = {
  setOneDriveOverwriteOptionSelected,
  resetDuplicateFilesAndOverWriteSettings,
  setNeedsRedirectToHome,
  resetWorkingDirectories,
  resetLocationBeforeSaving,
  addWorkingDirectories
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface DuplicateFilesProps extends PropsFromRedux {
  showSelectVersion?: boolean;
  onCancel?: () => void;
  hasDuplicateFolders?: boolean;
  mode
}

const DuplicateFiles: React.FunctionComponent<DuplicateFilesProps> = (props: DuplicateFilesProps) => {
  const languageStrings = React.useContext(LanguageContext);

  const [isFileOpenLocally, setIsFileOpenLocally] = React.useState(false);

  const [options, setOptions] = React.useState<IChoiceGroupOption[]>([
    { key: OneDriveOverWriteEnum.rename.toString(), text: languageStrings.Rename },
    { key: OneDriveOverWriteEnum.replace.toString(), text: languageStrings.Replace }
  ]);

  const [folderOptions] = React.useState<IChoiceGroupOption[]>([
    { key: OneDriveOverWriteEnum.rename.toString(), text: languageStrings.Rename }
  ]);

  React.useEffect(() => {
    const currentFileUniqueId = CacheHelper.get(DocubirdCache.currentFileUniqueId);
    if (props.mode === ModeEnum.office && props.files[0]?.filePreCommitResult?.uniqueId === currentFileUniqueId) {
      setIsFileOpenLocally(true);
    } else {
      setIsFileOpenLocally(false);
    }
  }, [props.mode, props.files]);

  React.useEffect(() => {
    setOptions([
      { key: OneDriveOverWriteEnum.rename.toString(), text: languageStrings.Rename },
      { key: OneDriveOverWriteEnum.replace.toString(), text: languageStrings.Replace, disabled: (props.hasLockedCheckedOutFiles || isFileOpenLocally) }
    ])
  }, [props.hasLockedCheckedOutFiles, isFileOpenLocally]);

  React.useEffect(() => {
    props.setOneDriveOverwriteOptionSelected(OneDriveOverWriteEnum.rename);
  }, []);

  const handleRadioSelection = (ev: React.FormEvent<HTMLInputElement>, option: IChoiceGroupOption): void => {
    let selectedProp: string = OneDriveOverWriteEnum[option.key]; // to avoid language collissions
    props.setOneDriveOverwriteOptionSelected(OneDriveOverWriteEnum[selectedProp]);
  };

  const cancelDuplicateSave = () => {
    if (props.needsRedirect) {
      props.setNeedsRedirectToHome(false);
      props.resetWorkingDirectories();
    }

    if (props.onCancel) {
      props.onCancel();
    }

    props.setOneDriveOverwriteOptionSelected(OneDriveOverWriteEnum.notSelected);
    //props.resetDuplicateFilesAndOverWriteSettings(props.files);
    goToLastPageBeforeSavingForSeachedFolders();
  };

  const goToLastPageBeforeSavingForSeachedFolders = () => {
    if (props.oneDriveLocationsBeforeSaving?.length > 0) {
      props.resetWorkingDirectories(props.oneDriveLocationsBeforeSaving);
      props.resetLocationBeforeSaving();
    }
  };

  return (
    <Stack styles={!props.showSelectVersion ? stackStylesDefault : stackStylesPadded} tokens={stackTokens}>
      {props.hasLockedCheckedOutFiles &&
        <MessageBar messageBarType={MessageBarType.warning} isMultiline={false}>
          {languageStrings.OneOrMoreFilesLockedCheckedOut}
        </MessageBar>}
      {props.hasDuplicateEmails
        ? <ChoiceGroup
          defaultSelectedKey={OneDriveOverWriteEnum.rename.toString()}
          options={options}
          onChange={handleRadioSelection}
          label={languageStrings.RenameTextFileOption}
          styles={choiceGroupStyles}
          required={true}
        />
        : <Label>{languageStrings.DuplicateResolved}</Label>
      }
      {props.hasDuplicateFolders &&
        <ChoiceGroup
          defaultSelectedKey={OneDriveOverWriteEnum.rename.toString()}
          options={folderOptions}
          label={languageStrings.FolderSameNameAutoRename}
          styles={choiceGroupStyles}
          required={true}
        />}
      {!props.showSelectVersion &&
        <Stack.Item>
          <PrimaryButton text={languageStrings.Cancel} allowDisabledFocus onClick={cancelDuplicateSave} />
        </Stack.Item>}
    </Stack>
  )
};

export default connector(DuplicateFiles);
